import { useCallback, useEffect, useRef, useState } from 'react';
import { useFetch } from 'hooks/fetch'; // here we don’t need to cache result, so we can use fetch from hooks/fetch
import { useAppSelector, useAppStore } from 'store/hooks';
import { getOrderPreview } from '../actions';
import { getLimitPriceAndTotalCostForLimitBuy } from '../utils/format';
import { OrderType } from '../types';
import { selectTradingAccount } from '../reducers/selectors';
/**
 * This hook is used for market buy, limit buy(buy in shares) and market sell.
 */
const useOrderPreview = (assetId, tradingAccountId, options) => {
    const { operation, initialAmount, updateOnMount, fundOrderWithDirectDebit, fundOrderWithOpenBanking, cachedOrderPreview: cached, } = options;
    const [order, setOrder] = useState(cached);
    const [loading, setLoading] = useState(false);
    const [fetch] = useFetch();
    const latestAmount = useRef(initialAmount);
    const tradingAccount = useAppSelector(selectTradingAccount);
    const availableToInvest = tradingAccount?.account.availableToInvest;
    const store = useAppStore();
    const fetchOrderPreview = useCallback(async (amount, buyLimitOrderInfo, quantityInputRef) => {
        latestAmount.current = amount;
        if (amount > 0 && assetId) {
            setLoading(true);
            try {
                const result = await fetch(getOrderPreview(amount, assetId, tradingAccountId, operation, buyLimitOrderInfo, fundOrderWithDirectDebit, fundOrderWithOpenBanking) // will not dispatch anything to the store
                );
                if (latestAmount.current === amount) {
                    if (buyLimitOrderInfo?.orderType === OrderType.LIMIT && operation === 'buy') {
                        const currentPrice = store.getState().invest.currentPriceCaches[assetId];
                        if (currentPrice) {
                            const totalAmount = getLimitPriceAndTotalCostForLimitBuy(amount, currentPrice, result.order.estimated.fxConversion);
                            if (totalAmount.totalAmount.value && availableToInvest) {
                                if (totalAmount.totalAmount.value > availableToInvest.value) {
                                    quantityInputRef?.current?.clear();
                                }
                                else {
                                    setOrder(result);
                                }
                            }
                        }
                    }
                    else {
                        setOrder(result);
                    }
                    setLoading(false);
                }
            }
            catch {
                if (latestAmount.current === amount) {
                    // TODO: should also retry to getOrderPreview
                    setLoading(false);
                }
            }
        }
        else {
            setLoading(false);
            setOrder(undefined);
        }
    }, [assetId, availableToInvest, fundOrderWithDirectDebit, fundOrderWithOpenBanking, operation, tradingAccountId]);
    useEffect(() => {
        if (updateOnMount)
            fetchOrderPreview(initialAmount);
        // one-time event, on mount only
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateOnMount]);
    return [order, fetchOrderPreview, loading];
};
export default useOrderPreview;
